/* eslint-disable @next/next/no-img-element */
import { Fragment, ReactNode, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import RefreshSVG from '@/icons/RefreshSVG';
import RefreshSecondarySVG from '@/icons/RefreshSecondarySVG';
import { ClassNameProps } from '@/types/common.types';

import { useVerification } from '@/contexts/VerificationContext/useVerification';

import { TransformedSubscription } from '@/api/types/user.types';

import { normalizePrice } from '@/helpers/transformations.helpers';

import { useStore } from '@/contexts/StoreContext';
import styles from '@/contexts/VerificationContext/VerificationModal/SubscriptionVerification/SelectedSubscriptionInfo.module.scss';
import { useSubscriptionVerification } from '@/contexts/VerificationContext/VerificationModal/SubscriptionVerification/contexts';

// =================================================================

interface SelectedSubscriptionInfoProps extends ClassNameProps {
  subscription: TransformedSubscription | null;
}

interface subscriptionInfoLineProps {
  label: string;
  value?: ReactNode;
  size: 'sm' | 'md';
}

// =================================================================

export const SubscriptionInfoLine = (props: subscriptionInfoLineProps) => {
  const { label, value, size } = props;
  return (
    <li className={styles.subscriptionInfoLine}>
      <p className={clsx(styles.label, styles[size])}>{label}</p>
      <span className={styles.line} />
      <div className={clsx(styles.value, styles[size])}>{value}</div>
    </li>
  );
};

// =================================================================

export const SelectedSubscriptionInfo = (props: SelectedSubscriptionInfoProps) => {
  const { subscription } = props;

  const t = useTranslations('verification');

  const currDate = new Date().toLocaleDateString('en-GB');
  const { isRenew, setRenew } = useVerification();

  const { subscriptionPeriod } = useStore(store => store.subscriptionStore);

  const methods = useForm({
    defaultValues: {
      is_renew: isRenew,
      bonus: false,
    },
  });

  useEffect(() => {
    setRenew(methods.watch('is_renew'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch('is_renew')]);

  return (
    <FormProvider {...methods}>
      <div className={styles.subscriptionInfo}>
        <ul className={styles.subscriptionList}>
          <SubscriptionInfoLine
            label={t('tariffPrice')}
            value={`${normalizePrice(subscription?.period[subscriptionPeriod]?.price ?? 0)} UZS`}
            size="sm"
          />
          <SubscriptionInfoLine
            label={t('period')}
            value={t('days', {
              days: Number(subscription?.period[subscriptionPeriod]?.days),
            })}
            size="sm"
          />
          <SubscriptionInfoLine
            label={t('start')}
            value={`${currDate.replaceAll('/', '.')}`}
            size="sm"
          />
        </ul>
      </div>
    </FormProvider>
  );
};

// =================================================================
export const ResultSubscriptionInfo = (props: SelectedSubscriptionInfoProps) => {
  const { subscription, className } = props;

  const { subscriptionPeriod } = useStore(store => store.subscriptionStore);
  const t = useTranslations();

  const currDate = new Date().toLocaleDateString('en-GB');
  const { balance, writeOff } = useSubscriptionVerification();
  const { isRenew } = useVerification();

  return (
    <div className={clsx(styles.subscriptionInfo, className)}>
      <h2 className={styles.title}>{`${normalizePrice(writeOff) || 0} UZS`}</h2>
      <p className={styles.description}>{t('verification.paidForPlan')}</p>
      <ul className={styles.resultSubscriptionList}>
        <SubscriptionInfoLine
          label={t('verification.planName')}
          value={subscription?.title}
          size="sm"
        />
        <SubscriptionInfoLine
          label={t('verification.tariffPrice')}
          value={`${normalizePrice(subscription?.period[subscriptionPeriod]?.price ?? 0)} UZS`}
          size="sm"
        />
        <SubscriptionInfoLine
          label={t('verification.period')}
          value={t('verification.days', {
            days: Number(subscription?.period[subscriptionPeriod]?.days ?? 0),
          })}
          size="sm"
        />
        <SubscriptionInfoLine
          label={t('verification.start')}
          value={`${currDate.replaceAll('/', '.')}`}
          size="sm"
        />
        <SubscriptionInfoLine
          label={t('verification.autoRenewal')}
          value={
            <span
              className={clsx(styles.label, {
                [styles.active]: isRenew,
              })}
            >
              {isRenew ? (
                <Fragment>
                  {t('verification.enabled')} <RefreshSVG height={16} width={16} />
                </Fragment>
              ) : (
                <Fragment>
                  {t('verification.disabled')} <RefreshSecondarySVG />
                </Fragment>
              )}
            </span>
          }
          size="sm"
        />
        <SubscriptionInfoLine
          label={t('page.userProfile.balance')}
          value={`${normalizePrice(balance || 0)} UZS`}
          size="sm"
        />
      </ul>
    </div>
  );
};
